import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { theme } from '../theme';
import { LogoCompact, PasswordVisibilityToggle } from '../Icons';
import { checkPasswordStrength, PASSWORD_REQUIREMENTS_MESSAGE, PasswordStrengthMeter } from '../PasswordStrength';
import AccountScreenBanner from './AccountScreenBanner';

// ResetPasswordScreen — new (Aug 31, per Ali's report). Real, standalone
// gap found live: Supabase's password-recovery email link creates a
// valid authenticated session and hands control to the app to show a
// "set your new password" form — but no such form existed anywhere in
// this codebase. The recovery link was landing at the app's Site URL,
// getting treated as a normal login by App.js's routing (authenticated
// user -> straight to /dashboard), and the password itself was never
// actually being changed at all, even though the person ended up
// looking "logged in."
//
// This page is only ever reached via App.js's own PASSWORD_RECOVERY
// event detection forcing a navigate() here — see App.js for that half
// of the fix. Deliberately mirrors Login.js's card/input/button
// classes (login-card, login-input-wrap, btn-primary login-submit-btn)
// rather than introducing new styling, so this reads as the same
// product, not a bolted-on utility screen.
function ResetPasswordScreen({ onDone }) {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async () => {
    setError(null);

    // Extended to the shared rule (Sep 16, per Ali's report — "at all
    // levels," same requirement as signup) — this only checked length
    // before, letter+number weren't enforced here at all.
    const strength = checkPasswordStrength(password);
    if (!strength.valid) {
      setError(PASSWORD_REQUIREMENTS_MESSAGE);
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }

    setSubmitting(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setSubmitting(false);

    if (updateError) {
      setError(updateError.message || 'Could not update your password. Try again.');
      return;
    }

    setDone(true);
    // Clears App.js's isPasswordRecovery flag so normal routing rules
    // (authenticated -> dashboard) apply again from here on — without
    // this, the person would be stuck being redirected back to this
    // page on every navigation, since the recovery flag would never
    // otherwise clear.
    onDone();
    // Real bug found live (Aug 31, per Ali's report): the copy below
    // always said "Taking you to your Dashboard..." but nothing ever
    // actually navigated there — clearing isPasswordRecovery has no
    // effect on its own; AppRoutes' redirect effect only ever handles
    // the "recovery just started" direction (navigate TO this page),
    // not "recovery just finished" (navigate AWAY from it). Fixed by
    // navigating directly here instead of relying on that effect for
    // both directions. Short delay so the confirmation message is
    // actually readable rather than an instant, jarring redirect.
    setTimeout(() => navigate('/dashboard', { replace: true }), 1200);
  };

  return (
    <>
      <AccountScreenBanner section="Change Password" />
      <div className="login-form-panel" style={{ minHeight: 'calc(100vh - 60px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="login-card yedge">
          <div className="login-card-body">
            <div className="login-card-icon">
            <LogoCompact width={150} color="#1E3A5F" />
          </div>
          <h2 style={{ textAlign: 'center', margin: '0 0 4px', color: theme.textPrimary, fontFamily: theme.fontDisplay }}>
            Set a new password
          </h2>
          <p style={{ textAlign: 'center', color: theme.textMuted, margin: '0 0 24px' }}>
            {done ? "You're all set." : 'Choose a new password for your account.'}
          </p>

          {!done ? (
            <>
              <label>New password</label>
              <div className="login-input-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <PasswordVisibilityToggle visible={showPassword} onClick={() => setShowPassword(v => !v)} />
              </div>

              {/* Same shared meter as signup (Sep 16, per Ali's report
                  — "at all levels," identical component so the
                  requirement reads the same everywhere it applies). */}
              <PasswordStrengthMeter password={password} />

              <label>Confirm new password</label>
              <div className="login-input-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <PasswordVisibilityToggle visible={showPassword} onClick={() => setShowPassword(v => !v)} />
              </div>

              {error && (
                <p style={{ fontSize: '12.5px', color: theme.danger, marginTop: '8px', marginBottom: 0, fontWeight: 600 }}>{error}</p>
              )}

              <button
                className="btn-primary login-submit-btn"
                style={{ marginTop: '20px', width: '100%' }}
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? 'Saving…' : 'Save new password'}
              </button>
            </>
          ) : (
            <p style={{ textAlign: 'center', fontSize: '13px', color: theme.textPrimary, lineHeight: 1.6 }}>
              Your password has been updated. Taking you to your Dashboard…
            </p>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

export default ResetPasswordScreen;